import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { WavesIcon, GlassWaterIcon, SunsetIcon } from 'lucide-react';
export function PoolPage() {
  const {
    t
  } = useLanguage();
  return <motion.div initial={{
    opacity: 0,
    filter: 'blur(4px)'
  }} animate={{
    opacity: 1,
    filter: 'blur(0px)'
  }} exit={{
    opacity: 0,
    filter: 'blur(4px)'
  }} transition={{
    duration: 0.6,
    ease: [0.4, 0, 0.2, 1]
  }} data-id="element-496">
      <PageHeader title={t.pool.title} subtitle={t.pool.subtitle} backgroundImage="https://cdn.magicpatterns.com/uploads/4oUEUynuZPi1L5oRki7KwM/download_(3).png" data-id="element-497" />

      {/* Hero Gallery Section */}
      <section className="py-24 lg:py-32 bg-warmWhite" data-id="element-498">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10" data-id="element-499">
          <div className="text-center mb-20" data-id="element-500">
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex items-center justify-center gap-5 mb-6" data-id="element-501">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-sand/50" data-id="element-502"></div>
              <span className="text-sand text-[11px] font-medium tracking-[0.35em] uppercase" data-id="element-503">
                {t.pool.galleryTitle}
              </span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-sand/50" data-id="element-504"></div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8" data-id="element-505">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="md:col-span-12 lg:col-span-8 h-[400px] lg:h-[600px] relative rounded-sm overflow-hidden group shadow-elegant" data-id="element-506">
              <img src="https://cdn.magicpatterns.com/uploads/4oUEUynuZPi1L5oRki7KwM/download_(3).png" alt="Vista aérea del restaurante al atardecer" className="w-full h-full object-cover transition-transform duration-[1.5s] ease-elegant group-hover:scale-105" data-id="element-507" />
              <div className="absolute inset-0 bg-sunset-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 mix-blend-overlay" data-id="element-508" />
            </motion.div>

            <div className="md:col-span-12 lg:col-span-4 flex flex-col gap-6 lg:gap-8" data-id="element-509">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8,
              delay: 0.15
            }} className="h-[300px] lg:h-[284px] relative rounded-sm overflow-hidden group shadow-elegant" data-id="element-510">
                <img src="https://cdn.magicpatterns.com/uploads/pMgaJM1usm7E8sUALYHgtT/WhatsApp_Image_2026-04-27_at_16.58.06.jpg" alt="Terraza y piscina" className="w-full h-full object-cover transition-transform duration-[1.5s] ease-elegant group-hover:scale-105" data-id="element-511" />
                <div className="absolute inset-0 bg-sunset-rose/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 mix-blend-overlay" data-id="element-512" />
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8,
              delay: 0.3
            }} className="h-[300px] lg:h-[284px] relative rounded-sm overflow-hidden group shadow-elegant" data-id="element-513">
                <img src="https://cdn.magicpatterns.com/uploads/rHjP7fGanNXL6Fibv5UAnJ/WhatsApp_Image_2026-04-27_at_16.58.18.jpg" alt="Jardín mediterráneo" className="w-full h-full object-cover transition-transform duration-[1.5s] ease-elegant group-hover:scale-105" data-id="element-514" />
                <div className="absolute inset-0 bg-sunset-deep/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 mix-blend-overlay" data-id="element-515" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-32 lg:py-44 relative overflow-hidden" data-id="element-516">
        {/* Video Background — Pool water reflections */}
        <div className="absolute inset-0 w-full h-full z-0" data-id="element-517">
          <video autoPlay loop muted playsInline className="object-cover w-full h-full opacity-35 scale-105" data-id="element-518">
            <source src="https://videos.pexels.com/video-files/2491284/2491284-uhd_2560_1440_24fps.mp4" type="video/mp4" data-id="element-519" />
          </video>
          <div className="absolute inset-0 bg-warmWhite/88 backdrop-blur-[2px]" data-id="element-520" />
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 relative z-10" data-id="element-521">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-28" data-id="element-522">
            {/* Text Side */}
            <motion.div initial={{
            opacity: 0,
            x: -35
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 1,
            ease: [0.4, 0, 0.2, 1]
          }} className="w-full lg:w-7/12 flex flex-col justify-center" data-id="element-523">
              <div className="flex items-center gap-5 mb-8" data-id="element-524">
                <div className="h-px w-16 bg-gradient-to-r from-sand to-sand/30" data-id="element-525"></div>
                <span className="text-sand text-[11px] font-medium tracking-[0.35em] uppercase" data-id="element-526">
                  {t.pool.subtitle}
                </span>
              </div>

              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-navy mb-12 leading-[1.1]" data-id="element-527">
                {t.pool.title}
              </h2>

              <div className="space-y-8 text-dark/55 font-light leading-[2] text-[15px]" data-id="element-528">
                <p data-id="element-529">{t.pool.description1}</p>
                <p data-id="element-530">{t.pool.description2}</p>
              </div>

              <div className="mt-16 flex items-center gap-6" data-id="element-531">
                <div className="h-px w-24 bg-gradient-to-r from-sand/40 to-transparent" data-id="element-532"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-sand/40" data-id="element-533"></div>
              </div>
            </motion.div>

            {/* Image Side */}
            <motion.div initial={{
            opacity: 0,
            x: 35
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 1,
            delay: 0.15,
            ease: [0.4, 0, 0.2, 1]
          }} className="w-full lg:w-5/12 relative" data-id="element-534">
              <div className="relative aspect-[3/4] w-full rounded-sm overflow-hidden shadow-elegant" data-id="element-535">
                <img src="https://cdn.magicpatterns.com/uploads/bqmFNx8L7g7urQSioN5d6M/WhatsApp_Image_2026-04-27_at_16.57.59.jpg" alt="Piscina con sombrillas" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-536" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/15 to-transparent" data-id="element-537" />
              </div>
              {/* Decorative accent frame */}
              <div className="absolute -bottom-6 -left-6 w-full h-full border border-sand/20 rounded-sm -z-10" data-id="element-538"></div>
              {/* Warm glow */}
              <div className="absolute -top-10 -right-10 w-48 h-48 bg-sunset-gold/10 rounded-full blur-3xl -z-10" data-id="element-539"></div>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 section-divider" data-id="element-540" />
      </section>

      {/* Features Section */}
      <section className="py-32 lg:py-44 bg-navy text-white relative overflow-hidden" data-id="element-541">
        {/* Warm ambient glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-sunset-gold/[0.03] rounded-full blur-[120px] pointer-events-none" data-id="element-542" />

        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-10 relative z-10" data-id="element-543">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20" data-id="element-544">
            {[{
            icon: WavesIcon,
            title: t.pool.featureTitle1,
            desc: t.pool.featureDesc1
          }, {
            icon: GlassWaterIcon,
            title: t.pool.featureTitle2,
            desc: t.pool.featureDesc2
          }, {
            icon: SunsetIcon,
            title: t.pool.featureTitle3,
            desc: t.pool.featureDesc3
          }].map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 25
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.15,
            duration: 0.8
          }} className="text-center" data-id="element-545">
                <div className="w-14 h-14 mx-auto bg-sand/10 rounded-full flex items-center justify-center mb-8 ring-1 ring-sand/10" data-id="element-546">
                  <item.icon className="w-6 h-6 text-sand" data-id="element-547" />
                </div>
                <h3 className="font-serif text-xl mb-5" data-id="element-548">{item.title}</h3>
                <p className="text-white/45 font-light leading-[1.85] text-sm" data-id="element-549">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-44 lg:py-56 overflow-hidden" data-id="element-550">
        <div className="absolute inset-0 w-full h-full" data-id="element-551">
          <img src="https://cdn.magicpatterns.com/uploads/noRonfkFvEGEVmZXgXRQ4r/download_(4).png" alt="El Astillero de noche con piscina iluminada" className="w-full h-full object-cover scale-105" data-id="element-552" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/60 to-navy/80 backdrop-blur-[2px]" data-id="element-553" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center" data-id="element-554">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="flex items-center justify-center gap-5 mb-7" data-id="element-555">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-sand-light/50" data-id="element-556"></div>
            <span className="text-sand-light text-[11px] font-medium tracking-[0.35em] uppercase" data-id="element-557">
              {t.pool.subtitle}
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-sand-light/50" data-id="element-558"></div>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1,
          duration: 0.8
        }} className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-white mb-12" data-id="element-559">
            {t.pool.title}
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} data-id="element-560">
            <Link to="/reservations" className="inline-block px-14 py-5 bg-sand hover:bg-sand-dark text-navy font-medium text-[11px] tracking-[0.2em] uppercase transition-all duration-700 ease-elegant hover:shadow-warm hover:scale-[1.02]" data-id="element-561">
              {t.pool.cta}
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>;
}